import { z } from 'zod';

/**
 * Alfabeto Crockford base32 usado por ULID (sin I, L, O, U). 26 caracteres.
 * Los ids de Fermin son `<prefijo>_<ULID>` (constitution I; research R-10).
 */
export const ULID_PATTERN = '[0-9A-HJKMNP-TV-Z]{26}';

/**
 * Prefijos de id. 001: `cmp`, `site`, `adm`, `ses`, `tok`, `aud`.
 * 002 (research R-11): `wrk`, `dev`, `inv`, `wss`, `rbq`, `pna`.
 * 003 (research R-12, data-model.md "Identificadores nuevos"): `trm`, `tss`, `tac`, `tap`.
 * 004 (research R-14, data-model.md "Identificadores"): `chl`, `mrk`.
 * 007 (contracts/errors-permissions-audit.md §1): `reg`.
 * 008 (contracts/errors-permissions-audit.md §1): `sht`, `shv`, `spt`, `wsp`, `sha`, `hol`, `abs`,
 * `jus`, `eva`, `tsc`.
 * 009 (contracts/errors-permissions-audit.md §1): `itg`, `xid`, `xrc`, `syn`, `syi`, `rvw`.
 * Ningún otro prefijo existe.
 *
 * `dev` no es un ULID aleatorio: es `crockford32(trunc128(SHA-256(SPKI)))` de la clave pública
 * del dispositivo (26 caracteres, mismo alfabeto), así que valida con `idSchema('dev')`.
 *
 * `tss` es además el `tsid` público del Bootstrap Code (`FM1B:<ULID>:<BT>`, sin el prefijo).
 */
export const ID_PREFIXES = {
  company: 'cmp',
  site: 'site',
  admin: 'adm',
  session: 'ses',
  token: 'tok',
  audit: 'aud',
  worker: 'wrk',
  device: 'dev',
  invitation: 'inv',
  workerSession: 'wss',
  rebindRequest: 'rbq',
  privacyAcceptance: 'pna',
  terminal: 'trm',
  terminalSession: 'tss',
  terminalActivationCode: 'tac',
  terminalAutonomousPeriod: 'tap',
  /** Challenge VPP. Los frames NO llevan un prefijo del ULID (filtraría su marca temporal): llevan
   * un `c` derivado por hash — ver `challengeShortId` en `modules/vpp/tokens.ts`. */
  vppChallenge: 'chl',
  /** Marca de asistencia. El orden lexicográfico sirve de desempate salvo empate en el mismo
   * milisegundo: `ulid()` no es la variante monótona (desvío documentado en data-model.md). */
  attendanceMark: 'mrk',
  /** 007 · solicitud de regularización de una marca (`mark_regularization_requests`). */
  regularizationRequest: 'reg',
  // --- 008 · horarios y tareo ---
  /** Plantilla de turno (`shift_templates`). */
  shiftTemplate: 'sht',
  /** Versión de una plantilla (`shift_template_versions`, append-only). */
  shiftTemplateVersion: 'shv',
  /** Patrón semanal o cíclico (`schedule_patterns`). */
  schedulePattern: 'spt',
  /** Aplicación de un patrón a un trabajador (`worker_schedule_patterns`). */
  workerSchedulePattern: 'wsp',
  /** Versión del plan de un día (`shift_assignments`, append-only). */
  shiftAssignment: 'sha',
  /** Feriado de empresa o de sede (`holidays`). */
  holiday: 'hol',
  /** Ausencia registrada (`worker_absences`). */
  absence: 'abs',
  /** Justificación de tardanza, salida anticipada o falta (`attendance_justifications`). */
  justification: 'jus',
  /** Versión de la evaluación de un día (`day_evaluations`, append-only). */
  dayEvaluation: 'eva',
  /** Cierre de período del tareo (`timesheet_closures`). */
  timesheetClosure: 'tsc',
  // --- 009 · integración genérica con sistemas externos ---
  /** Conexión con el sistema externo, una por empresa (`integrations`). */
  integration: 'itg',
  /** Vínculo id externo ↔ sede o trabajador de Fermin (`external_identities`). */
  externalIdentity: 'xid',
  /** Horario, ausencia o feriado externo normalizado (`integration_records`). */
  integrationRecord: 'xrc',
  /** Ejecución de sincronización (`integration_sync_runs`). */
  syncRun: 'syn',
  /** Resultado por registro de una ejecución (`integration_sync_items`). */
  syncItem: 'syi',
  /** Caso de la bandeja de revisión (`integration_reviews`). */
  integrationReview: 'rvw',
} as const;

export type IdPrefix = (typeof ID_PREFIXES)[keyof typeof ID_PREFIXES];

/**
 * Esquema Zod para un id prefijado: valida `^${prefix}_[0-9A-HJKMNP-TV-Z]{26}$`.
 * @example idSchema('cmp').parse('cmp_01ARZ3NDEKTSV4RRFFQ69G5FAV')
 */
export function idSchema<P extends string>(prefix: P) {
  const regex = new RegExp(`^${prefix}_${ULID_PATTERN}$`);
  return z
    .string()
    .regex(regex, { message: `Identificador inválido: se espera ${prefix}_<ULID>` })
    .describe(`Id con prefijo ${prefix}_ seguido de un ULID`);
}

export const CompanyId = idSchema(ID_PREFIXES.company);
export const SiteId = idSchema(ID_PREFIXES.site);
export const AdminId = idSchema(ID_PREFIXES.admin);
export const SessionId = idSchema(ID_PREFIXES.session);
export const TokenId = idSchema(ID_PREFIXES.token);
export const AuditId = idSchema(ID_PREFIXES.audit);
export const WorkerId = idSchema(ID_PREFIXES.worker);
export const DeviceId = idSchema(ID_PREFIXES.device);
export const InvitationId = idSchema(ID_PREFIXES.invitation);
export const WorkerSessionId = idSchema(ID_PREFIXES.workerSession);
export const RebindRequestId = idSchema(ID_PREFIXES.rebindRequest);
export const PrivacyAcceptanceId = idSchema(ID_PREFIXES.privacyAcceptance);
export const TerminalId = idSchema(ID_PREFIXES.terminal);
export const TerminalSessionId = idSchema(ID_PREFIXES.terminalSession);
export const TerminalActivationCodeId = idSchema(ID_PREFIXES.terminalActivationCode);
export const TerminalAutonomousPeriodId = idSchema(ID_PREFIXES.terminalAutonomousPeriod);
export const VppChallengeId = idSchema(ID_PREFIXES.vppChallenge);
export const AttendanceMarkId = idSchema(ID_PREFIXES.attendanceMark);
export const RegularizationRequestId = idSchema(ID_PREFIXES.regularizationRequest);
export const ShiftTemplateId = idSchema(ID_PREFIXES.shiftTemplate);
export const ShiftTemplateVersionId = idSchema(ID_PREFIXES.shiftTemplateVersion);
export const SchedulePatternId = idSchema(ID_PREFIXES.schedulePattern);
export const WorkerSchedulePatternId = idSchema(ID_PREFIXES.workerSchedulePattern);
export const ShiftAssignmentId = idSchema(ID_PREFIXES.shiftAssignment);
export const HolidayId = idSchema(ID_PREFIXES.holiday);
export const AbsenceId = idSchema(ID_PREFIXES.absence);
export const JustificationId = idSchema(ID_PREFIXES.justification);
export const DayEvaluationId = idSchema(ID_PREFIXES.dayEvaluation);
export const TimesheetClosureId = idSchema(ID_PREFIXES.timesheetClosure);
export const IntegrationId = idSchema(ID_PREFIXES.integration);
export const ExternalIdentityId = idSchema(ID_PREFIXES.externalIdentity);
export const IntegrationRecordId = idSchema(ID_PREFIXES.integrationRecord);
export const SyncRunId = idSchema(ID_PREFIXES.syncRun);
export const SyncItemId = idSchema(ID_PREFIXES.syncItem);
export const IntegrationReviewId = idSchema(ID_PREFIXES.integrationReview);

export type CompanyId = z.infer<typeof CompanyId>;
export type SiteId = z.infer<typeof SiteId>;
export type AdminId = z.infer<typeof AdminId>;
export type SessionId = z.infer<typeof SessionId>;
export type TokenId = z.infer<typeof TokenId>;
export type AuditId = z.infer<typeof AuditId>;
export type WorkerId = z.infer<typeof WorkerId>;
export type DeviceId = z.infer<typeof DeviceId>;
export type InvitationId = z.infer<typeof InvitationId>;
export type WorkerSessionId = z.infer<typeof WorkerSessionId>;
export type RebindRequestId = z.infer<typeof RebindRequestId>;
export type PrivacyAcceptanceId = z.infer<typeof PrivacyAcceptanceId>;
export type TerminalId = z.infer<typeof TerminalId>;
export type TerminalSessionId = z.infer<typeof TerminalSessionId>;
export type TerminalActivationCodeId = z.infer<typeof TerminalActivationCodeId>;
export type TerminalAutonomousPeriodId = z.infer<typeof TerminalAutonomousPeriodId>;
export type VppChallengeId = z.infer<typeof VppChallengeId>;
export type AttendanceMarkId = z.infer<typeof AttendanceMarkId>;
export type RegularizationRequestId = z.infer<typeof RegularizationRequestId>;
export type ShiftTemplateId = z.infer<typeof ShiftTemplateId>;
export type ShiftTemplateVersionId = z.infer<typeof ShiftTemplateVersionId>;
export type SchedulePatternId = z.infer<typeof SchedulePatternId>;
export type WorkerSchedulePatternId = z.infer<typeof WorkerSchedulePatternId>;
export type ShiftAssignmentId = z.infer<typeof ShiftAssignmentId>;
export type HolidayId = z.infer<typeof HolidayId>;
export type AbsenceId = z.infer<typeof AbsenceId>;
export type JustificationId = z.infer<typeof JustificationId>;
export type DayEvaluationId = z.infer<typeof DayEvaluationId>;
export type TimesheetClosureId = z.infer<typeof TimesheetClosureId>;
export type IntegrationId = z.infer<typeof IntegrationId>;
export type ExternalIdentityId = z.infer<typeof ExternalIdentityId>;
export type IntegrationRecordId = z.infer<typeof IntegrationRecordId>;
export type SyncRunId = z.infer<typeof SyncRunId>;
export type SyncItemId = z.infer<typeof SyncItemId>;
export type IntegrationReviewId = z.infer<typeof IntegrationReviewId>;
